import { useRef } from "react";

/**
 * Below two, the control takes about as much room as the replies it hides and
 * costs a click to read them.
 */
export const MIN_HIDDEN_REPLIES = 2;

/**
 * Threads whose length has been judged, and when.
 *
 * Module-level because the decision has to outlive the card: the anchored card
 * remounts whenever a reply refetches the pin list it is drawn from, and a
 * decision held in the component is re-derived from the now-longer thread. An
 * unmount can't clear this, since that remount looks identical to a close. Age
 * separates them.
 */
const judged = new Map<string, { isCollapsible: boolean; at: number }>();

/** Longer than a remount, shorter than reopening a thread by hand. */
const REMOUNT_GRACE_MS = 1000;

/**
 * Whether a thread should offer to collapse its middle, decided once per visit.
 *
 * Collapsing answers "am I coming back to a long conversation?", so re-deriving
 * it as replies arrive would fold a thread up around the person reading it.
 */
export function useThreadCollapse({
  threadId,
  replyCount,
  isEnabled,
  now = () => Date.now(),
}: {
  threadId: string;
  replyCount: number;
  /** Only the anchored card collapses; the rail shows one comment. */
  isEnabled: boolean;
  /** Injectable for tests, which need to age an entry without waiting. */
  now?: () => number;
}): boolean {
  const decided = useRef<boolean | null>(null);

  // The rail renders every thread and stays mounted, so stamping from there
  // would keep entries fresh forever and hand a card opened later its judgment
  // of a shorter thread.
  if (!isEnabled) {
    return false;
  }

  if (decided.current === null) {
    const remembered = judged.get(threadId);
    const isFresh =
      remembered !== undefined && now() - remembered.at < REMOUNT_GRACE_MS;

    decided.current = isFresh
      ? remembered.isCollapsible
      : Math.max(replyCount - 1, 0) >= MIN_HIDDEN_REPLIES;
  }

  // Stamped every render, so the entry ages only once the card is gone.
  judged.set(threadId, { isCollapsible: decided.current, at: now() });

  return decided.current;
}
